import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, Tag } from "@phosphor-icons/react";
import { useLocation } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "ruta360_cupon_visto";
const HIDDEN_PATHS = ["/privacidad", "/terminos", "/devoluciones"];

export const CouponPopup = () => {
  const [show, setShow] = useState(false);
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY)) return;
    const path = location.pathname;
    if (
      path.startsWith("/checkout") ||
      path.startsWith("/pago") ||
      path.startsWith("/admin") ||
      HIDDEN_PATHS.includes(path)
    ) {
      return;
    }
    let shown = false;
    const trigger = () => {
      if (!shown) {
        shown = true;
        setShow(true);
      }
    };
    const t = setTimeout(trigger, 10000);
    const onScroll = () => {
      const h = document.documentElement;
      if (h.scrollTop + window.innerHeight >= h.scrollHeight * 0.5) trigger();
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      clearTimeout(t);
      window.removeEventListener("scroll", onScroll);
    };
  }, [location.pathname]);

  const close = () => {
    localStorage.setItem(STORAGE_KEY, "1");
    setShow(false);
  };

  useEffect(() => {
    if (!show) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/leads`, { email });
      setDone(true);
      localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      setDone(true);
      localStorage.setItem(STORAGE_KEY, "1");
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid="coupon-popup"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[1300] bg-black/60 flex items-center justify-center p-4"
          onClick={close}
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            transition={{ type: "spring", damping: 22, stiffness: 260 }}
            onClick={(e) => e.stopPropagation()}
            data-lenis-prevent
            style={{ maxHeight: "90dvh" }}
            className="relative w-[92vw] max-w-sm max-h-[90vh] overflow-y-auto bg-[#FFF9EF] border border-black/10 shadow-2xl text-center rounded-2xl"
          >
            <button
              data-testid="coupon-close-button"
              onClick={close}
              aria-label="Cerrar"
              className="absolute top-[10px] right-[10px] z-20 w-11 h-11 rounded-full bg-white text-black shadow-md flex items-center justify-center transition-colors hover:bg-black hover:text-white"
            >
              <X size={20} weight="bold" />
            </button>
            <div className="bg-black px-8 py-5 flex justify-center pointer-events-none">
              <img src="/images/logo-oficial.png" alt="Ruta 360" className="h-16 object-contain" />
            </div>
            <div className="p-8 pt-6">
            <span className="inline-flex items-center gap-2 bg-black text-white text-[10px] px-4 py-1.5 mb-6 rounded-full">
              <Tag size={12} weight="fill" /> Oferta de bienvenida
            </span>

            {!done ? (
              <>
                <h2 className="font-display text-6xl font-semibold uppercase leading-none text-[#111418]">
                  10% <span className="text-[#FF5508]">de descuento</span>
                </h2>
                <p className="text-black/60 text-sm mt-4 mb-6 leading-relaxed">
                  En tu primera compra. Déjanos tu correo y te mostramos tu cupón al instante.
                </p>
                <form data-testid="coupon-form" onSubmit={submit} className="flex flex-col gap-3">
                  <input
                    data-testid="coupon-email-input"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Tu correo electrónico"
                    className="w-full bg-white border border-black/15 px-4 py-3.5 text-sm outline-none focus:border-[#FF5508] transition-colors text-center rounded-xl"
                  />
                  <button
                    data-testid="coupon-submit-button"
                    type="submit"
                    disabled={sending}
                    className="w-full bg-[#FF5508] text-white text-xs py-4 rounded-full transition-colors duration-300 hover:bg-black disabled:opacity-50"
                  >
                    {sending ? "Enviando…" : "Obtener cupón"}
                  </button>
                </form>
                <button
                  type="button"
                  data-testid="coupon-no-thanks"
                  onClick={close}
                  className="mt-4 text-black/45 text-xs underline underline-offset-2 hover:text-black transition-colors"
                >
                  No, gracias
                </button>
              </>
            ) : (
              <div data-testid="coupon-success" className="py-6">
                <p className="font-display text-3xl uppercase font-semibold text-[#111418] mb-3">¡Listo!</p>
                <p className="text-black/60 text-sm leading-relaxed">
                  Te enviamos tu cupón del 10% a tu correo.
                </p>
              </div>
            )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
